import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Image as ImageIcon, Trash, X } from "@phosphor-icons/react";
import MobileShell from "@/mobile/MobileShell";
import MobileCameraCapture from "@/mobile/MobileCameraCapture";
import { getToken } from "@/lib/authToken";
export default function MobileExpedienteFotos() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const galleryRef = useRef(null);
  const cameraFallbackRef = useRef(null);
  const token = getToken();

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/expedientes/${id}/attachments`, { params: { kind: "photo" } });
      setItems(r.data || []);
    } catch (e) {
      toast.error("No se pudieron cargar las fotos");
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => {
        if (f.type?.startsWith("image/")) fd.append("files", f);
      });
      fd.append("kind", "photo");
      await api.post(`/expedientes/${id}/attachments/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await reload();
      const n = Array.from(files).filter((f) => f.type?.startsWith("image/")).length;
      toast.success(`${n} foto${n === 1 ? "" : "s"} subida${n === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo");
    } finally {
      setUploading(false);
    }
  };

  // Subida individual usada por la cámara en-app (una foto por petición,
  // en background mientras el perito sigue disparando).
  const uploadOnePhoto = async (blob) => {
    const fd = new FormData();
    const ts = Date.now();
    const file = blob instanceof File
      ? blob
      : new File([blob], `foto-${ts}.jpg`, { type: blob.type || "image/jpeg" });
    fd.append("files", file);
    fd.append("kind", "photo");
    const r = await api.post(`/expedientes/${id}/attachments/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    // El endpoint devuelve {created: N, attachments: [...]}.
    const list = r.data?.attachments || [];
    return list[0]?.id || null;
  };

  const deleteOnePhoto = async (attId) => {
    if (!attId) return;
    await api.delete(`/expedientes/${id}/attachments/${attId}`);
  };

  const supportsInAppCamera =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof window !== "undefined" &&
    // Sólo en https (o localhost) las APIs de media funcionan
    (window.isSecureContext || /^(localhost|127\.)/.test(window.location.hostname));

  const openCamera = () => {
    if (supportsInAppCamera) {
      setCameraOpen(true);
    } else {
      // Fallback: input file con capture nativo
      cameraFallbackRef.current?.click();
    }
  };

  const del = async (attId) => {
    if (!confirm("¿Eliminar esta foto?")) return;
    try {
      await api.delete(`/expedientes/${id}/attachments/${attId}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <MobileShell title={`Fotos · ${items.length}`} backTo={`/m/expediente/${id}`}>
      {/* Toolbar Cámara + Galería */}
      <div className="px-3 pt-3 grid grid-cols-2 gap-2">
        <button
          onClick={openCamera}
          className="inline-flex items-center justify-center gap-2 bg-foreground text-white text-[14px] font-semibold py-3 rounded-md"
          data-testid="mobile-fotos-camera"
        >
          <Camera className="w-5 h-5" weight="fill" /> Cámara
        </button>
        <button
          onClick={() => galleryRef.current?.click()}
          className="inline-flex items-center justify-center gap-2 bg-white border border-[#E8EAED] text-foreground text-[14px] font-semibold py-3 rounded-md"
          data-testid="mobile-fotos-gallery"
        >
          <ImageIcon className="w-5 h-5" weight="bold" /> Galería
        </button>
        {/* Fallback input para navegadores sin getUserMedia */}
        <input
          ref={cameraFallbackRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => upload(e.target.files)}
        />
      </div>

      {uploading && (
        <div className="px-3 pt-2 text-[12px] text-muted text-center">Subiendo…</div>
      )}

      {/* Grid de fotos */}
      <div className="px-3 pt-3 grid grid-cols-3 gap-1">
        {items.length === 0 && !uploading && (
          <div className="col-span-3 text-center text-muted text-[12px] py-10">Sin fotos. Toma la primera.</div>
        )}
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => setPreview(it)}
            className="aspect-square overflow-hidden rounded-md border border-[#E8EAED] bg-zinc-100"
            data-testid={`mobile-foto-${it.id}`}
          >
            <img
              src={`${API}/expedientes/${id}/attachments/${it.id}/file?token=${token}`}
              alt={it.filename}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Preview lightbox */}
      {preview && (
        <div className="fixed inset-0 z-30 bg-black flex flex-col" data-testid="foto-preview">
          <div className="flex items-center justify-between p-3 text-white">
            <span className="text-[12px] truncate">{preview.filename}</span>
            <div className="flex gap-2">
              <button
                onClick={() => { del(preview.id); setPreview(null); }}
                className="p-2 rounded-md hover:bg-white/10"
                aria-label="Eliminar"
                data-testid="foto-preview-del"
              >
                <Trash className="w-5 h-5" weight="bold" />
              </button>
              <button
                onClick={() => setPreview(null)}
                className="p-2 rounded-md hover:bg-white/10"
                aria-label="Cerrar"
                data-testid="foto-preview-close"
              >
                <X className="w-5 h-5" weight="bold" />
              </button>
            </div>
          </div>
          <div className="flex-1 flex items-center justify-center px-3 pb-3">
            <img
              src={`${API}/expedientes/${id}/attachments/${preview.id}/file?token=${token}`}
              alt={preview.filename}
              className="max-h-full max-w-full object-contain"
            />
          </div>
        </div>
      )}
      {/* Cámara en-app a pantalla completa (ráfaga continua, subida en background) */}
      {cameraOpen && (
        <MobileCameraCapture
          uploadOne={uploadOnePhoto}
          deleteOne={deleteOnePhoto}
          onCancel={async () => {
            setCameraOpen(false);
            // Recargar grid: puede haber fotos ya subidas en background
            await reload();
          }}
          onDone={async (attIds) => {
            setCameraOpen(false);
            const n = (attIds || []).length;
            await reload();
            if (n > 0) {
              toast.success(`${n} foto${n === 1 ? "" : "s"} guardada${n === 1 ? "" : "s"}`);
            }
          }}
        />
      )}
    </MobileShell>
  );
}
